// CardGroup component - Displays grouped cards with shared visual styling
// Implements US-2.2: Card Grouping

import React, { useState } from 'react';
import { Card as CardType, CardGroup as CardGroupType } from '../types/card';
import { Card } from './Card';
import styles from './CardGroup.module.css';

interface CardGroupProps {
  group: CardGroupType;
  cards: CardType[];
  isFacilitator: boolean;
  isCollectionPhase: boolean;
  sessionId: string | null;
  onUpdateCard: (cardId: string, text: string) => void;
  onDeleteCard: (cardId: string) => void;
  onToggleExpand: (cardId: string) => void;
  onToggleDiscussed: (cardId: string) => void;
  onUpdateGroupLabel: (groupId: string, label: string) => void;
  onUngroupCard: (cardId: string) => void;
  onDeleteGroup: (groupId: string) => void;
}

const CardGroup: React.FC<CardGroupProps> = ({
  group,
  cards,
  isFacilitator,
  isCollectionPhase,
  sessionId,
  onUpdateCard,
  onDeleteCard,
  onToggleExpand,
  onToggleDiscussed,
  onUpdateGroupLabel,
  onUngroupCard,
  onDeleteGroup,
}) => {
  const [isEditingLabel, setIsEditingLabel] = useState(false);
  const [labelInput, setLabelInput] = useState(group.label || '');

  // HANDLE label edit save
  const handleSaveLabel = () => {
    if (labelInput.trim() !== (group.label || '')) {
      onUpdateGroupLabel(group.id, labelInput.trim());
    }
    setIsEditingLabel(false);
  };

  // HANDLE label edit cancel
  const handleCancelLabel = () => {
    setLabelInput(group.label || '');
    setIsEditingLabel(false);
  };

  return (
    <div className={styles.cardGroup}>
      {/* Group header with label and controls */}
      <div className={styles.cardGroupHeader}>
        {isEditingLabel ? (
          <div className={styles.cardGroupLabelEdit}>
            <input
              type="text"
              value={labelInput}
              onChange={(e) => setLabelInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveLabel();
                if (e.key === 'Escape') handleCancelLabel();
              }}
              placeholder="Gruppen-Label (optional)"
              maxLength={100}
              autoFocus
              className={styles.cardGroupLabelInput}
            />
            <button
              onClick={handleSaveLabel}
              className={`${styles.cardGroupLabelBtn} ${styles.save}`}
              title="Label speichern"
            >
              ✓
            </button>
            <button
              onClick={handleCancelLabel}
              className={`${styles.cardGroupLabelBtn} ${styles.cancel}`}
              title="Abbrechen"
            >
              ×
            </button>
          </div>
        ) : (
          <div className={styles.cardGroupLabel}>
            <span className={styles.cardGroupLabelText}>
              {group.label || 'Gruppierte Karten'}
            </span>
            {isFacilitator && (
              <button
                onClick={() => setIsEditingLabel(true)}
                className={`${styles.cardGroupLabelBtn} ${styles.edit}`}
                title="Gruppen-Label bearbeiten"
              >
                ✏️
              </button>
            )}
          </div>
        )}

        {isFacilitator && (
          <div className={styles.cardGroupActions}>
            <button
              onClick={() => onDeleteGroup(group.id)}
              className={styles.cardGroupDeleteBtn}
              title="Alle Gruppierungen auflösen"
            >
              Gruppierung auflösen
            </button>
          </div>
        )}
      </div>

      {/* Grouped cards */}
      <div className={styles.cardGroupCards}>
        {cards.map((card) => (
          <div key={card.id} className={styles.cardGroupCardWrapper}>
            <Card
              card={card}
              isOwner={sessionId === card.authorSessionId}
              isFacilitator={isFacilitator}
              isCollecting={isCollectionPhase}
              onUpdate={onUpdateCard}
              onDelete={onDeleteCard}
              onToggleExpand={onToggleExpand}
              onToggleDiscussed={onToggleDiscussed}
            />
            {isFacilitator && (
              <button
                onClick={() => onUngroupCard(card.id)}
                className={styles.cardUngroupBtn}
                title="Aus Gruppe entfernen"
              >
                ×
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CardGroup;
